import axios from 'axios';
import i18n from '../i18n';
import { prepareDocumentUploadFile } from '../utils/imageUploadCompression';

/** Rejection shaped like axios error so upload callers surface `detail` consistently. */
function rejectUploadPrepare(message) {
  const err = new Error(message);
  err.response = { data: { detail: message } };
  return Promise.reject(err);
}

/**
 * True when REACT_APP_API_URL points at a backend on this machine (direct calls, no CORS issue).
 */
function isLocalBackendUrl(url) {
  if (!url || !String(url).trim()) return false;
  try {
    const u = new URL(url);
    return u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

/**
 * Backend API base URL.
 * - Dev + remote API (e.g. https://dev.globalmobility.law/api): use `/api` so CRA `setupProxy.js`
 *   forwards to the real host — browser stays same-origin and avoids CORS.
 * - Dev + local API: `REACT_APP_API_URL=http://localhost:8000/api` uses that URL directly.
 * - Production: `REACT_APP_API_URL` or same-host `/api`.
 */
function getApiBaseUrl() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  const isDev = typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

  if (isDev && envUrl && !isLocalBackendUrl(envUrl)) {
    return '/api';
  }
  if (envUrl) return envUrl;
  if (isDev) return '/api';
  return '/api';
}

/**
 * Origin of the backend (OAuth redirects must hit the real API, not localhost:3000).
 * Order: REACT_APP_API_URL → REACT_APP_PROXY_TARGET → dev default → window (prod same-site).
 */
function getBackendOrigin() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
    try {
      return new URL(envUrl).origin;
    } catch {
      // fall through
    }
  }
  const proxyTarget = (process.env.REACT_APP_PROXY_TARGET || '').trim();
  if (proxyTarget.startsWith('http')) {
    try {
      return new URL(proxyTarget).origin;
    } catch {
      // fall through
    }
  }
  if (process.env.NODE_ENV === 'development') {
    return 'https://dev.globalmobility.law';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

const API_DETAIL_TRANSLATIONS_ES = {
  'Authentication required': 'Autenticacion requerida',
  'Invalid token': 'Token invalido',
  'Admin access required': 'Se requiere acceso de administrador',
  'Internal server error': 'Error interno del servidor',
  'User not found': 'Usuario no encontrado',
  'Session not found': 'Sesion no encontrada',
  'Case not found': 'Caso no encontrado',
  'Document not found': 'Documento no encontrado',
  'Invalid case reference': 'Referencia de caso invalida',
  'Invalid document id': 'ID de documento invalido',
  'Invalid status': 'Estado invalido',
  'Message is required': 'El mensaje es obligatorio',
  'Field name is required': 'El nombre del campo es obligatorio',
  'Text is required': 'El texto es obligatorio',
  'No file selected': 'No se selecciono ningun archivo',
  'File not found in storage': 'Archivo no encontrado en el almacenamiento',
  'Could not read file from storage': 'No se pudo leer el archivo del almacenamiento',
  'Document storage unavailable': 'El almacenamiento de documentos no esta disponible',
  'File is not available for download (not stored in S3).': 'El archivo no esta disponible para descargar (no esta almacenado en S3).',
  'File is not available (not stored in S3).': 'El archivo no esta disponible (no esta almacenado en S3).',
  'Invalid or expired code': 'Codigo invalido o vencido',
  'Google Sign-In is not configured': 'Google Sign-In no esta configurado',
  'Email is required.': 'El correo electronico es obligatorio.',
  'This email is already registered.': 'Este correo electronico ya esta registrado.',
  'This phone number is already registered.': 'Este numero de telefono ya esta registrado.',
};

function currentLanguage() {
  const raw = (i18n.language || 'es').split('-')[0].toLowerCase();
  return raw === 'en' ? 'en' : 'es';
}

function localizeApiDetail(detail) {
  const lang = currentLanguage();
  if (lang !== 'es' || detail == null) return detail;
  if (typeof detail === 'string') return API_DETAIL_TRANSLATIONS_ES[detail] || detail;
  if (Array.isArray(detail)) return detail.map((item) => localizeApiDetail(item));
  if (typeof detail === 'object') {
    const next = { ...detail };
    if (typeof next.msg === 'string') next.msg = API_DETAIL_TRANSLATIONS_ES[next.msg] || next.msg;
    return next;
  }
  return detail;
}

const SITUATION_SUB_ROUTES = new Set(['work', 'family', 'vulnerability']);
const SITUATION_SOURCE_SUB_ROUTES = new Set(['7A', '7B', '7C', '8', '9', '10', '11', '12']);
const SITUATION_PRORROGA_TYPES = new Set(['jobSearch', 'integrationReport', 'illness', 'disability', 'retirement']);

function legacyRouteFromSourceSubRoute(raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value === '7A') return 'work';
  if (value === '7B') return 'family';
  if (value === '7C') return 'vulnerability';
  return null;
}

/**
 * Keep the situation branch payload self-consistent before it leaves the browser.
 * This is used by both the step submit endpoint (snake_case) and autosave (camelCase)
 * so Mongo never keeps stale asylum-only / irregular-only values when the answer flips.
 */
function normalizeSituationBranchFields(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return details;

  const next = { ...details };

  const normalizeWorkHistory = (raw) => {
    const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    return ['worked', 'current', 'offer'].includes(value) ? value : null;
  };

  if (Object.prototype.hasOwnProperty.call(next, 'asylum_applicant')) {
    if (next.asylum_applicant === true) {
      const oar = typeof next.oar_case_number === 'string' ? next.oar_case_number.trim() : '';
      next.oar_case_number = oar || null;
      next.asylum_scenario =
        typeof next.asylum_scenario === 'string' && ['1', '2', '3', '4', '5', '6'].includes(next.asylum_scenario)
          ? next.asylum_scenario
          : null;
      next.prorroga_type =
        typeof next.prorroga_type === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorroga_type)
          ? next.prorroga_type
          : null;
      next.sub_route = null;
      next.irregular_sub_route = null;
      next.work_history = null;
      next.work_type = null;
      next.self_employment_activity = null;
      next.qualifying_ground = null;
    } else if (next.asylum_applicant === false) {
      const subRoute = typeof next.sub_route === 'string' ? next.sub_route.trim() : '';
      next.sub_route = SITUATION_SOURCE_SUB_ROUTES.has(subRoute) ? subRoute : null;
      const legacyRoute =
        typeof next.irregular_sub_route === 'string' ? next.irregular_sub_route.trim().toLowerCase() : '';
      next.irregular_sub_route = SITUATION_SUB_ROUTES.has(legacyRoute)
        ? legacyRoute
        : legacyRouteFromSourceSubRoute(next.sub_route);
      next.oar_case_number = null;
      next.asylum_scenario = null;
      next.prorroga_type =
        typeof next.prorroga_type === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorroga_type)
          ? next.prorroga_type
          : null;
      next.work_history = next.irregular_sub_route === 'work' ? normalizeWorkHistory(next.work_history) : null;
      next.work_type =
        next.sub_route === '7A' && ['employed', 'selfemployed'].includes(String(next.work_type ?? '').trim())
          ? String(next.work_type).trim()
          : null;
      next.self_employment_activity =
        next.sub_route === '7A' && typeof next.self_employment_activity === 'string'
          ? next.self_employment_activity.trim() || null
          : null;
    }
  }

  if (Object.prototype.hasOwnProperty.call(next, 'asylumApplicant')) {
    const asylum = String(next.asylumApplicant ?? '').trim().toLowerCase();
    if (asylum === 'yes') {
      next.oarCaseNumber = typeof next.oarCaseNumber === 'string' ? next.oarCaseNumber.trim() : '';
      next.asylumScenario =
        typeof next.asylumScenario === 'string' && ['1', '2', '3', '4', '5', '6'].includes(next.asylumScenario)
          ? next.asylumScenario
          : '';
      next.prorrogaType =
        typeof next.prorrogaType === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorrogaType)
          ? next.prorrogaType
          : '';
      next.subRoute = '';
      next.irregularSubRoute = '';
      next.workHistory = '';
      next.workType = '';
      next.selfEmploymentActivity = '';
      next.qualifyingGround = '';
    } else if (asylum === 'no') {
      const subRoute = typeof next.subRoute === 'string' ? next.subRoute.trim() : '';
      next.subRoute = SITUATION_SOURCE_SUB_ROUTES.has(subRoute) ? subRoute : '';
      const route =
        typeof next.irregularSubRoute === 'string' ? next.irregularSubRoute.trim().toLowerCase() : '';
      next.irregularSubRoute = SITUATION_SUB_ROUTES.has(route)
        ? route
        : legacyRouteFromSourceSubRoute(next.subRoute) || '';
      next.oarCaseNumber = '';
      next.asylumScenario = '';
      next.prorrogaType =
        typeof next.prorrogaType === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorrogaType)
          ? next.prorrogaType
          : '';
      next.workHistory = next.irregularSubRoute === 'work' ? (normalizeWorkHistory(next.workHistory) || '') : '';
      next.workType =
        next.subRoute === '7A' && ['employed', 'selfemployed'].includes(String(next.workType ?? '').trim())
          ? String(next.workType).trim()
          : '';
      next.selfEmploymentActivity =
        next.subRoute === '7A' && typeof next.selfEmploymentActivity === 'string'
          ? next.selfEmploymentActivity.trim()
          : '';
    } else {
      next.oarCaseNumber = '';
      next.asylumScenario = '';
      next.subRoute = '';
      next.prorrogaType = '';
      next.irregularSubRoute = '';
      next.workHistory = '';
      next.workType = '';
      next.selfEmploymentActivity = '';
    }
  }

  return next;
}

api.interceptors.request.use((config) => {
  const lng = currentLanguage();
  config.headers['Accept-Language'] = lng;
  // Public signup/landing calls must not send a stale admin/applicant Bearer token.
  if (config.public === true) return config;
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.data && Object.prototype.hasOwnProperty.call(err.response.data, 'detail')) {
      err.response.data.detail = localizeApiDetail(err.response.data.detail);
    }
    if (err.response?.status === 401) {
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('immigrationSessionId');
      } catch {
        // ignore
      }
      if (typeof window !== 'undefined') {
        const path = window.location?.pathname || '';
        const isLoginPath = path === '/login' || path === '/es/login';
        if (!isLoginPath) {
          window.location.assign('/es/login');
        }
      }
    }
    return Promise.reject(err);
  }
);

/** Full API: auth, session, chat, data, livechat, admin (same surface as OG frontend). */
export const mvpApi = {
  // --- Auth (unchanged) ---
  getPartnerByToken: (token) =>
    api.get(`/partners/by-token/${encodeURIComponent(String(token).trim())}`, { public: true }),
  /** Public: active users registered for this E.164 phone (sales funnel signup check). */
  getUsersByPhone: (phone) =>
    api.get('/auth/users/by-phone', { params: { phone }, public: true }),
  requestPhoneOtp: (phone, channel = 'sms', intent = 'signup') =>
    api.post('/auth/phone/request-otp', { phone, channel, intent }),
  verifyPhoneOtp: (phone, code, intent = 'signup', referralCode = null, partnerToken = null, browserLanguage = null) =>
    api.post('/auth/phone/verify-otp', {
      phone,
      code,
      intent,
      referral_code: referralCode || undefined,
      partner_token: partnerToken || undefined,
      browser_language: browserLanguage || undefined,
    }),
  getMe: () => api.get('/auth/me'),
  updatePersonalDetails: (details) => api.put('/auth/personal-details', details),
  updateEligibilityRouting: (details) =>
    api.put('/auth/eligibility-routing', normalizeSituationBranchFields(details)),
  updateSituationDetails: (details) =>
    api.put('/auth/situation-details', normalizeSituationBranchFields(details)),
  /**
   * Partial autosave (Mongo personal_data). Address keys match Address step UI:
   * street→domicilio, streetNumber→numero_domicilio, floorPiso→piso, floorDoor→puerta,
   * city→localidad, province→provincia, postalCode→codigo_postal, country→pais_residencia,
   * residenceSince→residente_desde (send {month,year}; empty both clears when key present).
   */
  applicantAutosave: (details) =>
    api.put('/auth/applicant-autosave', normalizeSituationBranchFields(details)),
  markApplicantCaseNeedsAttention: (sessionId) =>
    api.post(`/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/mark-needs-attention`),
  acknowledgeDocumentSection: (sessionId, section) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/document-section-acknowledged`,
      { section }
    ),
  unacknowledgeDocumentSection: (sessionId, section) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/document-section-unacknowledged`,
      { section }
    ),
  acknowledgeProofOfResidence: (sessionId) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/proof-of-residence-acknowledged`
    ),
  sendApplicantHelpMessage: (sessionId, message, section) =>
    api.post(`/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/help-message`, { message, section: section || undefined }),
  /** Passport/identity + DOB, sex, nationality, etc. Single PUT; `identity_doc_expiry_at` and `nie` optional. */
  updateDocumentDetails: (details) => api.put('/auth/document-details', details),
  /** AI OCR extraction from uploaded passport or identity document image/PDF. */
  analyzePassportDocument: async (file, documentType = 'passport') => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    formData.append('document_type', documentType);
    return api.post('/auth/document-details/passport-ai', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  /**
   * Multi-image document scan → single PDF (`responseType` blob).
   *
   * @param {File[]} files
   * @param {boolean} [ocrEnabled=true]  Add OCR text layer (Tesseract/OCRmyPDF) on the server.
   * @param {string}  [ocrLanguages]     ``+``-joined Tesseract languages (e.g. ``"eng+spa"``).
   *                                     Defaults to backend ``OCR_LANGUAGES`` env (``"eng+spa"``).
   * @param {boolean[]|null} [skipAutoCropFlags]  One boolean per file; true skips server axis-aligned auto-crop for that page (e.g. already cropped on device).
   */
  processDocumentScan: (files, ocrEnabled = true, ocrLanguages = null, skipAutoCropFlags = null) => {
    const fd = new FormData();
    const list = files || [];
    list.forEach((f) => fd.append('images', f));
    fd.append('ocr_enabled', ocrEnabled ? 'true' : 'false');
    if (ocrLanguages) fd.append('ocr_languages', String(ocrLanguages));
    if (
      Array.isArray(skipAutoCropFlags) &&
      skipAutoCropFlags.length === list.length &&
      list.length > 0
    ) {
      fd.append('skip_auto_crop_flags', JSON.stringify(skipAutoCropFlags));
    }
    return api.post('/scan/process', fd, {
      responseType: 'blob',
      /** Launch: fail fast on weak cellular; MobileDocumentScanner shows retry / cancel modal on transport errors. */
      timeout: 10000,
      headers: { 'Content-Type': 'multipart/form-data' },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  updatePersonalInfoDetails: (details) => api.put('/auth/personal-info-details', details),
  updateCivilFamilyDetails: (details) => api.put('/auth/civil-family-details', details),
  /**
   * Full address step save. Body snake_case: street_name, street_number, floor_piso, floor_door (puerta),
   * city_locality, province, postal_code, country, residence_since (YYYY-MM-01 or null).
   */
  updateAddressDetails: (details) => api.put('/auth/address-details', details),
  updateEngagementDetails: (details) => api.put('/auth/engagement-details', details),
  requestProfilePhoneOtp: (phone, channel = 'sms') =>
    api.post('/auth/profile/request-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      channel,
    }),
  verifyProfilePhoneOtp: (phone, code) =>
    api.post('/auth/profile/verify-phone-otp', { phone: phone.startsWith('+') ? phone : `+${phone}`, code }),
  requestProfileEmailOtp: (email) => api.post('/auth/profile/request-email-otp', { email }),
  verifyProfileEmailOtp: (email, code) =>
    api.post('/auth/profile/verify-email-otp', { email, code }),
  getMyReferrals: () => api.get('/auth/me/referrals'),
  applyMyReferralCode: (referralCode) =>
    api.post('/auth/me/referral-code', {
      referral_code: String(referralCode || '').trim().toUpperCase(),
    }),
  deleteAccount: () => api.delete('/auth/me'),

  // --- MVP admin: registered users ---
  getAdminRegistrations: (params) => api.get('/admin/registrations', { params }),
  getAdminAdvisors: () => api.get('/admin/advisors'),
  getPortalSettings: () => api.get('/admin/portal-settings'),
  putPortalSettings: (body) => api.put('/admin/portal-settings', body),
  patchAdminUserRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, { role }),
  patchAdminUserFollowUp: (userId, body) =>
    api.patch(`/admin/users/${userId}/follow-up`, body),
  patchAdminUserInquiry: (userId, body) =>
    api.patch(`/admin/users/${userId}/inquiry`, body),
  patchAdminCustomerDetails: (userId, body) =>
    api.patch(`/admin/users/${userId}/customer-details`, body),
  adminRequestPhoneOtp: (phone, channel = 'sms') =>
    api.post('/admin/users/request-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      channel,
    }),
  adminVerifyPhoneOtp: (phone, code) =>
    api.post('/admin/users/verify-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      code,
    }),
  adminRequestEmailOtp: (email) => api.post('/admin/users/request-email-otp', { email }),
  adminVerifyEmailOtp: (email, code) => api.post('/admin/users/verify-email-otp', { email, code }),
  bulkAssignUnassignedCases: (advisorUserId) =>
    api.post('/admin/cases/bulk-assign-unassigned', { advisor_user_id: advisorUserId }),
  createAdminUser: (data) => api.post('/admin/users', data),
  deleteAdminUser: (userId) => api.delete(`/admin/users/${userId}`),

  // --- Session (creates case in MongoDB when user starts) ---
  startSession: (data) => api.post('/session/start', data),
  getSession: (sessionId) => api.get(`/session/${sessionId}`),
  updateLanguage: (sessionId, language) =>
    api.put(`/session/${sessionId}/language`, { language }),
  listSessions: () => api.get('/sessions'),
  rejoinSession: (sessionId) => api.post(`/session/${sessionId}/rejoin`),
  completeForm: (sessionId) => api.post(`/session/${sessionId}/complete`),
  deleteSession: (sessionId) => api.delete(`/session/${sessionId}`),
  exportFormData: (sessionId, format = 'json') =>
    api.get(`/session/${sessionId}/export`, { params: { format } }),

  // --- Chat ---
  sendMessage: (sessionId, message) => api.post(`/chat/${sessionId}`, { message }),
  getMessages: (sessionId, limit = 50) =>
    api.get(`/chat/${sessionId}/messages`, { params: { limit } }),

  // --- Document upload ---
  /**
   * Upload a case document.
   * @param {string} sessionId
   * @param {File} file
   * @param {string|null} documentType - section id (e.g. passport_id)
   * @param {{
   *  caseOwnerUserId?: number|string,
   *  coverageDateFrom?: string,
   *  coverageDateTo?: string,
   *  residencyDocLabel?: string,
   *  selfEmploymentActivity?: string,
   *  supportingDescription?: string,
   *  criminalRecordCountry?: string
   * }} [opts] - Upload metadata; `caseOwnerUserId` is admin-only.
   */
  uploadDocument: async (sessionId, file, documentType = null, opts = {}) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    if (documentType) formData.append('document_type', documentType);
    if (opts.coverageDateFrom) formData.append('coverage_date_from', String(opts.coverageDateFrom));
    if (opts.coverageDateTo) formData.append('coverage_date_to', String(opts.coverageDateTo));
    if (opts.residencyDocLabel) formData.append('residency_doc_label', String(opts.residencyDocLabel));
    if (opts.selfEmploymentActivity) {
      formData.append('self_employment_activity', String(opts.selfEmploymentActivity).slice(0, 100));
    }
    if (opts.supportingDescription) {
      formData.append('supporting_description', String(opts.supportingDescription).slice(0, 500));
    }
    if (opts.criminalRecordCountry) {
      formData.append('criminal_record_country', String(opts.criminalRecordCountry).slice(0, 100));
    }
    if (opts.caseOwnerUserId != null && opts.caseOwnerUserId !== '') {
      formData.append('case_owner_user_id', String(opts.caseOwnerUserId));
    }
    return api.post(`/upload/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * Admin: explicit REST path (same behavior as uploadDocument + caseOwnerUserId).
   * @param {{
   *  coverageDateFrom?: string,
   *  coverageDateTo?: string,
   *  residencyDocLabel?: string,
   *  selfEmploymentActivity?: string,
   *  supportingDescription?: string,
   *  criminalRecordCountry?: string
   * }} [opts]
   */
  uploadAdminCaseDocument: async (userId, sessionId, file, documentType = null, opts = {}) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    if (documentType) formData.append('document_type', documentType);
    if (opts.coverageDateFrom) formData.append('coverage_date_from', String(opts.coverageDateFrom));
    if (opts.coverageDateTo) formData.append('coverage_date_to', String(opts.coverageDateTo));
    if (opts.residencyDocLabel) formData.append('residency_doc_label', String(opts.residencyDocLabel));
    if (opts.selfEmploymentActivity) {
      formData.append('self_employment_activity', String(opts.selfEmploymentActivity).slice(0, 100));
    }
    if (opts.supportingDescription) {
      formData.append('supporting_description', String(opts.supportingDescription).slice(0, 500));
    }
    if (opts.criminalRecordCountry) {
      formData.append('criminal_record_country', String(opts.criminalRecordCountry).slice(0, 100));
    }
    return api.post(`/admin/cases/${userId}/${sessionId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** Customer: presigned download URL for own session file (S3 only). */
  getDocumentDownloadUrl: (sessionId, documentId) =>
    api.get(`/documents/${sessionId}/${documentId}/download-url`),
  /** Admin: fresh presigned URL for any case file. */
  getAdminDocumentDownloadUrl: (userId, sessionId, documentId) =>
    api.get(`/admin/cases/${userId}/${sessionId}/documents/${documentId}/download-url`),
  /** Admin: stream file through API (JWT); avoids S3 presigned SignatureDoesNotMatch in browser. */
  downloadAdminCaseDocumentFile: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.get(`/admin/cases/${userId}/${sessionId}/documents/${id}/file`, {
      responseType: 'blob',
    });
  },
  /** Owner: stream file through API (JWT). */
  downloadSessionDocumentFile: (sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.get(`/documents/${sessionId}/${id}/file`, { responseType: 'blob' });
  },
  /** Owner: list session uploads (ids + section slots) for checklist sync after reload. */
  getSessionDocumentsSummary: (sessionId) =>
    api.get(`/documents/session/${sessionId}/summary`),
  updateSessionDocumentMetadata: (sessionId, documentId, payload) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.put(`/documents/${sessionId}/${id}/metadata`, payload);
  },
  /** Owner: hard-delete one uploaded document (Mongo + storage). */
  deleteSessionDocument: (sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.delete(`/documents/${sessionId}/${id}`);
  },

  // --- Data ---
  getCollectedData: (sessionId, formType = null) =>
    api.get(`/data/${sessionId}`, formType ? { params: { form_type: formType } } : undefined),
  exportData: (sessionId, format = 'json') =>
    api.get(`/data/${sessionId}/export`, { params: { format } }),
  updateField: (sessionId, field, value) =>
    api.put(`/data/${sessionId}/field`, { field, value }),

  // --- Forms ---
  getFormTypes: (language) => api.get('/forms', { params: { language } }),
  getFormFields: (formId, language) =>
    api.get(`/forms/${formId}/fields`, { params: { language } }),
  getFormHTML: (sessionId) =>
    api.get(`/session/${sessionId}/form/html`, { responseType: 'blob' }),

  // --- Live chat (AI + human handoff) ---
  startLiveChatSession: () => api.post('/livechat/session/start'),
  getLiveChatSession: (sessionId) => api.get(`/livechat/session/${sessionId}`),
  getLiveChatMessages: (sessionId) => api.get(`/livechat/session/${sessionId}/messages`),
  sendLiveChatMessage: (sessionId, content) =>
    api.post(`/livechat/session/${sessionId}/messages`, { content }),
  requestLiveChatHandoff: (sessionId, reason = null) =>
    api.post(`/livechat/session/${sessionId}/handoff`, { reason }),
  getLiveChatQueueStatus: (sessionId) =>
    api.get(`/livechat/session/${sessionId}/queue`),
  closeLiveChatSession: (sessionId) =>
    api.post(`/livechat/session/${sessionId}/close`),
  checkAgentAvailability: (signal) =>
    api.get('/livechat/availability/check', signal ? { signal } : {}),

  // --- Agent (live chat) ---
  getAgentAvailability: () => api.get('/livechat/agent/availability'),
  setAgentAvailability: (status, capacity = 1) =>
    api.post('/livechat/agent/availability', { status, capacity }),
  getAgentQueue: () => api.get('/livechat/agent/queue'),
  assignAgentSession: (sessionId) => api.post(`/livechat/agent/assign/${sessionId}`),
  getAgentActiveChats: () => api.get('/livechat/agent/active'),
  sendAgentMessage: (sessionId, content) =>
    api.post(`/livechat/agent/session/${sessionId}/messages`, { content }),
  closeAgentSession: (sessionId) =>
    api.post(`/livechat/agent/session/${sessionId}/close`),

  // --- Admin: cases (MongoDB sessions) ---
  getAllUserCases: (params) => api.get('/admin/cases', { params, timeout: 90000 }),
  getCaseByPublicId: (publicCaseId) =>
    api.get(`/admin/cases/by-public-id/${encodeURIComponent(String(publicCaseId || '').trim())}`),
  getUserCaseDetail: (userId, sessionId) =>
    api.get(`/admin/cases/${userId}/${sessionId}`),
  /** Admin: assign or unassign advisor on one case (`advisorUserId` null clears). */
  patchCaseAssignment: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/assignment`, body),
  patchCaseNeedsAttention: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/needs-attention`, body),
  /** Advisor metadata patch (referral, prior asylum, fee, workflow status). */
  patchCaseExtensions: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/case-extensions`, body),
  patchCaseStatus: (userId, sessionId, statusValue) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/case-extensions`, { case_status: statusValue }),
  lookupReferralCode: (code) =>
    api.get('/admin/referrals/lookup', { params: { code: String(code || '').trim() } }),
  lookupPartnerCode: (code) =>
    api.get('/admin/partners/lookup', { params: { code: String(code || '').trim() } }),
  lookupFeeCampaign: (code) =>
    api.get(`/admin/fee-campaigns/${encodeURIComponent(String(code || '').trim())}`),
  getCaseHistory: (userId, sessionId, config) =>
    api.get(`/admin/cases/${userId}/${sessionId}/history`, config),
  postCaseHistoryNote: (userId, sessionId, body) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/note`, body),
  markHelpMessageResponded: (userId, sessionId, messageId, replyText) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/help-responded/${messageId}`, {
      text: replyText ? String(replyText) : '',
    }),
  markFamilyInterestResponded: (userId, sessionId, messageId, replyText) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/family-interest-responded/${messageId}`, {
      text: replyText ? String(replyText) : '',
    }),
  /** Customer-level advisor timeline (not tied to one case session). */
  getUserProfileHistory: (userId, config) => api.get(`/admin/users/${userId}/history`, config),
  postUserProfileHistoryNote: (userId, body) =>
    api.post(`/admin/users/${userId}/history/note`, body),
  updateCaseFields: (userId, sessionId, fields) =>
    api.put(`/admin/cases/${userId}/${sessionId}/fields`, { fields }),
  getAdminExFormPreview: (userId, sessionId, formCode, download = false) =>
    api.get(`/admin/cases/${userId}/${sessionId}/forms/ex-preview`, {
      params: {
        form_code: formCode || undefined,
        download: download ? 'true' : 'false',
      },
      responseType: 'blob',
    }),
  autosaveAdminCaseDocumentDetails: (userId, sessionId, fields) =>
    api.put(`/admin/cases/${userId}/${sessionId}/documents/autosave`, { fields }),
  requestDocumentReupload: (userId, sessionId, documentType, reason) =>
    api.post(`/admin/cases/${userId}/${sessionId}/request-reupload`, {
      document_type: documentType,
      reason,
    }),
  updateAdminDocumentStatus: (userId, sessionId, documentId, statusValue, reason = null) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.put(`/admin/cases/${userId}/${sessionId}/documents/${id}/status`, {
      status: statusValue,
      reason: reason || undefined,
    });
  },
  deleteAdminDocument: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.delete(`/admin/cases/${userId}/${sessionId}/documents/${id}`);
  },
  restoreAdminDocument: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.post(`/admin/cases/${userId}/${sessionId}/documents/${id}/restore`);
  },

  // --- Admin: family groups ---
  createFamilyGroup: (familyName, leadContactUserId) =>
    api.post('/admin/family-groups', {
      family_name: familyName,
      lead_contact_user_id: leadContactUserId,
    }),
  getFamilyGroupByUser: (userId) =>
    api.get(`/admin/family-groups/by-user/${userId}`),
  getFamilyMembers: (familyGroupId) =>
    api.get(`/admin/family-groups/${familyGroupId}/members`),
  addFamilyMember: (familyGroupId, userId, relationshipToLead = null) =>
    api.post(`/admin/family-groups/${familyGroupId}/members`, {
      user_id: userId,
      relationship_to_lead: relationshipToLead || undefined,
    }),
};

export const API_BASE_URL = getApiBaseUrl();

export function getGoogleAuthUrl(intent = 'signup', partnerToken = null, referralCode = null) {
  const origin = getBackendOrigin();
  const params = new URLSearchParams();
  params.set('intent', intent);
  // Always show the Google account picker instead of silently reusing the active session.
  params.set('force_account_select', '1');
  if (partnerToken) params.set('partner_token', partnerToken);
  if (referralCode) params.set('referral_code', referralCode);
  return `${origin}/api/auth/google?${params.toString()}`;
}

export function getAppleAuthUrl(intent = 'signup', partnerToken = null, referralCode = null) {
  const origin = getBackendOrigin();
  const params = new URLSearchParams();
  params.set('intent', intent);
  // Ask backend to force Apple re-auth on login so stale provider sessions are not auto-reused.
  if (intent === 'login') params.set('force_account_select', '1');
  if (partnerToken) params.set('partner_token', partnerToken);
  if (referralCode) params.set('referral_code', referralCode);
  return `${origin}/api/auth/apple?${params.toString()}`;
}
